/**
 * Pilha de cards que o usuário reorganiza: arrasta pela alça, fecha no × e
 * volta ao arranjo original em "Reiniciar". O arranjo fica na sessão, junto do
 * fluxo, para não se desfazer ao trocar de menu.
 */

import { GripVertical, ListRestart, X } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { useSlotAcoes } from "@/components/layout/SlotAcoes";
import { Button } from "@/components/ui/button";
import { gravarSessao, lerSessao } from "@/lib/sessao";
import { cn } from "@/lib/utils";

export interface ItemCard {
  id: string;
  rotulo: string;
  /** `null`/`undefined`/`false` quando o card ainda não tem o que mostrar — some da pilha sem perder a vez. */
  conteudo: React.ReactNode;
}

interface Arranjo {
  ordem: string[];
  ocultos: string[];
}

/** Ordem salva + ids que surgiram depois (numa versão nova da página), na posição padrão. */
function completarOrdem(salva: string[], padrao: string[]): string[] {
  const conhecidos = salva.filter((id) => padrao.includes(id));
  for (const id of padrao) {
    if (conhecidos.includes(id)) continue;
    const i = padrao.indexOf(id);
    const anterior = padrao.slice(0, i).reverse().find((p) => conhecidos.includes(p));
    conhecidos.splice(anterior ? conhecidos.indexOf(anterior) + 1 : 0, 0, id);
  }
  return conhecidos;
}

const OrdemCards = React.createContext<string[] | null>(null);

/** Ordem atual dos cards da pilha (ids), para quem exporta "na ordem da tela". */
export function useOrdemCards() {
  return React.useContext(OrdemCards);
}

function mesmaLista(a: string[], b: string[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

export function CardsOrdenaveis({
  chave,
  itens,
  fixo = false,
}: {
  chave: string;
  itens: ItemCard[];
  /** Pilha fixa, na ordem padrão: sem "Reiniciar" e sem mover ou fechar cards. */
  fixo?: boolean;
}) {
  const chaveSessao = `ole:cards:${chave}`;
  const padrao = itens.map((item) => item.id);
  const padraoTexto = padrao.join("|");

  const [arranjo, setArranjo] = React.useState<Arranjo>(() => {
    const salvo = lerSessao<Arranjo>(chaveSessao);
    return {
      ordem: completarOrdem(Array.isArray(salvo?.ordem) ? salvo.ordem : [], padrao),
      ocultos: Array.isArray(salvo?.ocultos) ? salvo.ocultos : [],
    };
  });

  React.useEffect(() => {
    gravarSessao(chaveSessao, arranjo);
  }, [chaveSessao, arranjo]);

  // Garante a presença de ids novos sem descartar o que o usuário já arrumou.
  React.useEffect(() => {
    setArranjo((a) => {
      const ordem = completarOrdem(a.ordem, padraoTexto.split("|"));
      return mesmaLista(ordem, a.ordem) ? a : { ...a, ordem };
    });
  }, [padraoTexto]);

  /** Tira `id` da ordem e o recoloca antes (ou depois) de `alvo`. */
  const mover = React.useCallback((id: string, alvo: string, depois: boolean) => {
    if (id === alvo) return;
    setArranjo((a) => {
      const resto = a.ordem.filter((x) => x !== id);
      const i = resto.indexOf(alvo);
      if (i < 0) return a;
      resto.splice(depois ? i + 1 : i, 0, id);
      return mesmaLista(resto, a.ordem) ? a : { ...a, ordem: resto };
    });
  }, []);

  const ocultar = (id: string) =>
    setArranjo((a) => ({ ...a, ocultos: [...new Set([...a.ocultos, id])] }));

  const reiniciar = () => setArranjo({ ordem: padrao, ocultos: [] });

  const porId = new Map(itens.map((item) => [item.id, item]));
  const visiveis = arranjo.ordem
    .map((id) => porId.get(id))
    .filter((item): item is ItemCard => !!item && !arranjo.ocultos.includes(item.id))
    .filter((item) => item.conteudo != null && item.conteudo !== false);

  const alterado = arranjo.ocultos.length > 0 || !mesmaLista(arranjo.ordem, padrao);

  // Só o card cuja alça foi pressionada vira arrastável: o resto do card
  // continua livre para selecionar texto e arrastar as caixas do canvas.
  const [pegando, setPegando] = React.useState<string | null>(null);
  const [arrastando, setArrastando] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!pegando) return;
    const soltar = () => setPegando(null);
    window.addEventListener("pointerup", soltar);
    return () => window.removeEventListener("pointerup", soltar);
  }, [pegando]);

  const moverComTeclado = (evento: React.KeyboardEvent, id: string) => {
    if (evento.key !== "ArrowUp" && evento.key !== "ArrowDown") return;
    evento.preventDefault();
    const i = visiveis.findIndex((item) => item.id === id);
    const vizinho = visiveis[evento.key === "ArrowUp" ? i - 1 : i + 1];
    if (vizinho) mover(id, vizinho.id, evento.key === "ArrowDown");
    // O nó do botão é reaproveitado pelo React (mesma key), então o foco acompanha o card.
  };

  // Com abas na página, "Reiniciar" sobe para a linha delas; sem abas, fica aqui.
  const slot = useSlotAcoes();
  const acoes = (
    <>
      {arranjo.ocultos.length > 0 && (
        <span className="text-xs text-muted-foreground">
          {arranjo.ocultos.length === 1
            ? "1 card oculto"
            : `${arranjo.ocultos.length} cards ocultos`}
        </span>
      )}
      <Button
        variant="outline"
        size="sm"
        className="bg-card"
        onClick={reiniciar}
        disabled={!alterado}
        title="Volta à ordem original e reexibe os cards fechados"
      >
        <ListRestart />
        Reiniciar
      </Button>
    </>
  );

  return (
    <OrdemCards.Provider value={arranjo.ordem}>
      <div className="space-y-5">
        {fixo ? null : slot ? (
          createPortal(acoes, slot)
        ) : (
          <div className="nao-imprimir flex flex-wrap items-center justify-end gap-3">{acoes}</div>
        )}

        {visiveis.map((item) => (
          <div
            key={item.id}
            className={cn(
              "group relative transition-opacity duration-150",
              arrastando === item.id && "opacity-40",
            )}
            draggable={pegando === item.id}
            onDragStart={(evento) => {
              evento.dataTransfer.effectAllowed = "move";
              evento.dataTransfer.setData("text/plain", item.id);
              setArrastando(item.id);
            }}
            onDragEnd={() => {
              setArrastando(null);
              setPegando(null);
            }}
            onDragOver={(evento) => {
              if (!arrastando) return;
              evento.preventDefault();
              evento.dataTransfer.dropEffect = "move";
              const caixa = evento.currentTarget.getBoundingClientRect();
              mover(arrastando, item.id, evento.clientY > caixa.top + caixa.height / 2);
            }}
            onDrop={(evento) => evento.preventDefault()}
          >
            {item.conteudo}

            {/*
              Controles montados sobre a borda do card, e não dentro dele: os
              cabeçalhos já usam o canto direito para botões e selos próprios.
            */}
            {!fixo && (
              <div
                className={cn(
                  "nao-imprimir absolute -top-3 right-3 z-10 flex items-center gap-1 opacity-0 transition-opacity duration-150",
                  "focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
                  arrastando === item.id && "opacity-100",
                )}
              >
                <button
                  type="button"
                  aria-label={`Mover card ${item.rotulo} (setas ↑ ↓)`}
                  title="Arraste para mudar a ordem"
                  className="grid size-6 cursor-grab touch-none place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-baixa hover:text-foreground active:cursor-grabbing"
                  onPointerDown={() => setPegando(item.id)}
                  onKeyDown={(evento) => moverComTeclado(evento, item.id)}
                >
                  <GripVertical className="size-3.5" />
                </button>
                <button
                  type="button"
                  aria-label={`Fechar card ${item.rotulo}`}
                  title="Fechar card"
                  className="grid size-6 place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-baixa hover:border-destructive/40 hover:text-destructive"
                  onClick={() => ocultar(item.id)}
                >
                  <X className="size-3.5" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </OrdemCards.Provider>
  );
}
